// Lib
import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import * as Immutable from 'immutable';
import {
    getColorDefaultHexValue,
    getElementColorHexValue,
} from '../../../../../../../common/colors/elementColorFormatUtil';

// Utils
import {
    getFirstSelectedSecondaryColor,
    getSelectedElementColorStatusClass,
    getSelectedElementType,
} from '../colorToolUtils';
import { cx } from '@milanote/roma/utils';

// Selectors
import { getThemeIsDarkMode } from '../../../../../../user/account/accountModal/interface/themeSettings/themeSelector';
import {
    currentBoardDualColorListSelector,
    DualColor,
    DualColorList,
} from '../dualColors/currentBoardDualColorListSelector';

// Components
import ColorButton from './ColorButton';
import CustomColorPickerContainer from './CustomColorPickerContainer';
import ColorPopupModeSwitcher from './ColorPopupModeSwitcher';
import { hasEmptyColorValue } from '../colorButtonUtils';

// Styles
import './ColorPopupContent.scss';

// Constants
import { Color, COLORS } from '../../../../../../../common/colors/colorConstants';
import { COLOR_POPUP_MODE } from './colorPopupConstants';

// Types
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';

const COLORS_ARRAY = Object.values(COLORS);

/**
 * Some elements may persist predefined colors as hex values, while other's may pass the color name or CSS value.
 * Treat any of those representations as the same selected swatch.
 */
export const isDefaultColorSelected = (selectedColor: string | null, color: Color): boolean =>
    selectedColor === color.css || selectedColor === color.name || selectedColor === color.hex;

type ColorPopupContentProps = {
    supportBackgroundColor?: boolean;
    supportColorBar?: boolean;
    isDarkModeTheme: boolean;
    colorPopupMode?: string;
    openCustomColorPicker: () => void;
    selectedElements?: Immutable.List<ImMNElement>;
    suggestedColors: string[];
    dualColorList: DualColorList;
    defaultColors: Color[];
    setColorPopupMode?: (mode: string) => void;
    setColor: (color: string | null, secondaryColor?: string | null | undefined) => void;
    closePopup?: () => void;
    selectedColor: string | null;
    selectedColorHex?: string | null;
    supportDualColors?: boolean;
    maxSuggestedColors?: number;
    className?: string;
};

const colorPopupSelector = createStructuredSelector({
    isDarkModeTheme: getThemeIsDarkMode,
    dualColorList: currentBoardDualColorListSelector,
});

const areHexColorsEqual = (firstHex: string | null, secondHex: string | null) => {
    if (!firstHex || !secondHex) return false;

    return getColorDefaultHexValue(firstHex) === getColorDefaultHexValue(secondHex);
};

// This helper is used for cases where the color passed in has not been normalized to a hex
// value (eg. 'var(--color-element-blue)')
const isColorSelected = ({
    selectedColor,
    selectedColorHex,
    color,
    secondaryColor = null,
}: {
    selectedColor: string | null;
    selectedColorHex: string | null;
    color: DualColor | Color;
    secondaryColor?: string | null;
}) => {
    if ('primary' in color && 'secondary' in color) {
        return (
            (selectedColor === color.primary ||
                areHexColorsEqual(selectedColorHex, getElementColorHexValue(color.primary))) &&
            secondaryColor === color.secondary
        );
    }

    return (
        selectedColor === color.css ||
        selectedColor === color.name ||
        selectedColor === color.hex ||
        areHexColorsEqual(selectedColorHex, color.hex)
    );
};

/**
 * This is being moved to its own component so that it does not render unless the popup is open.
 */
const ColorPopupContent = ({
    supportBackgroundColor,
    supportColorBar,
    setColor,
    defaultColors = COLORS_ARRAY,
    selectedElements = Immutable.List<ImMNElement>(),
    selectedColor,
    suggestedColors = [],
    colorPopupMode,
    closePopup,
    openCustomColorPicker,
    setColorPopupMode,
    isDarkModeTheme,
    dualColorList = [],
    supportDualColors,
    maxSuggestedColors,
    selectedColorHex: selectedColorHexProp,
    className,
}: ColorPopupContentProps) => {
    const selectedType = getSelectedElementType(selectedElements);
    const selectedStatus = getSelectedElementColorStatusClass(selectedElements);
    const secondaryColor = getFirstSelectedSecondaryColor(selectedElements);
    const shouldUseSelectedColorHex = selectedColor || !hasEmptyColorValue(defaultColors);
    const selectedColorHex = shouldUseSelectedColorHex
        ? (selectedColorHexProp ?? getElementColorHexValue(selectedColor))
        : getElementColorHexValue(selectedColor);

    const preventUpdateSecondaryColor = colorPopupMode === COLOR_POPUP_MODE.COLOR_BAR || !supportDualColors;
    const showDualColorOptions = supportDualColors && colorPopupMode !== COLOR_POPUP_MODE.COLOR_BAR;

    return (
        <div className={cx('ColorPopupContent', className)}>
            {supportBackgroundColor && supportColorBar && setColorPopupMode && (
                <ColorPopupModeSwitcher colorPopupMode={colorPopupMode} setColorPopupMode={setColorPopupMode} />
            )}

            <ul className="colors">
                {defaultColors.map((color) => (
                    <li key={color.name}>
                        <ColorButton
                            selectedType={selectedType}
                            selectedStatus={selectedStatus}
                            setColor={setColor}
                            color={color}
                            colorPopupMode={colorPopupMode}
                            isActive={isColorSelected({ selectedColor, selectedColorHex, color })}
                            className={color.className}
                            isDarkModeTheme={isDarkModeTheme}
                            preventUpdateSecondaryColor={preventUpdateSecondaryColor}
                        />
                    </li>
                ))}
            </ul>

            {showDualColorOptions && (
                <>
                    <div className="divider" />
                    <ul className="colors">
                        {dualColorList.slice(0, maxSuggestedColors).map((color) => {
                            const { primary, secondary } = color;
                            return (
                                <li key={`${primary}${secondary}`}>
                                    <ColorButton
                                        selectedType={selectedType}
                                        selectedStatus={selectedStatus}
                                        setColor={setColor}
                                        color={primary}
                                        secondaryColor={secondary}
                                        colorPopupMode={colorPopupMode}
                                        isActive={
                                            isColorSelected({
                                                selectedColor,
                                                selectedColorHex,
                                                color,
                                                secondaryColor,
                                            }) && secondaryColor === secondary
                                        }
                                        isDarkModeTheme={isDarkModeTheme}
                                        preventUpdateSecondaryColor={preventUpdateSecondaryColor}
                                    />
                                </li>
                            );
                        })}
                    </ul>
                </>
            )}

            {suggestedColors.length > 0 && (
                <>
                    <div className="divider" />

                    <ul className="colors">
                        {suggestedColors.slice(0, maxSuggestedColors).map((color) => (
                            <li key={color}>
                                <ColorButton
                                    selectedType={selectedType}
                                    selectedStatus={selectedStatus}
                                    setColor={setColor}
                                    color={color}
                                    colorPopupMode={colorPopupMode}
                                    isActive={
                                        (selectedColor === color ||
                                            areHexColorsEqual(selectedColorHex, getElementColorHexValue(color))) &&
                                        (!secondaryColor || !showDualColorOptions)
                                    }
                                    isDarkModeTheme={isDarkModeTheme}
                                    preventUpdateSecondaryColor={preventUpdateSecondaryColor}
                                />
                            </li>
                        ))}
                    </ul>
                </>
            )}

            <CustomColorPickerContainer closePopup={closePopup} openCustomColorPicker={openCustomColorPicker} />
        </div>
    );
};

export default connect(colorPopupSelector)(ColorPopupContent);
